import * as THREE from "three/webgpu";
import { DrillEffect } from "~/engine/drill-effects";
import { GameEngine } from "~/engine/instance";
import type { Vector2 } from "~/state/game-types";
import { disposeMesh } from "~/utils/three";

export class GrenadeEntity {
  public readonly position: Vector2 = { x: 0, y: 0 };
  public readonly velocity: Vector2 = { x: 0, y: 0 };

  public readonly size: number;

  private readonly mesh: THREE.Mesh;
  private ringMesh?: THREE.Mesh;
  public readonly drillEffect = new DrillEffect();

  private readonly createdAtMs: number;
  private explodedAtMs: number | null = null;

  constructor(spawnPos: Vector2) {
    const voxelSize = GameEngine.voxelTerrain.voxelSize;
    this.size = voxelSize; // exactly 1 voxel square

    this.position.x = spawnPos.x;
    this.position.y = spawnPos.y;

    const geom = new THREE.PlaneGeometry(this.size, this.size);
    const mat = new THREE.MeshBasicNodeMaterial({
      color: new THREE.Color(0xff0000),
      transparent: true,
    });

    this.mesh = new THREE.Mesh(geom, mat);
    this.mesh.renderOrder = 90; // above terrain
    this.mesh.position.set(this.position.x, this.position.y, 90);

    GameEngine.renderer.registerEntity(this.mesh);

    this.createdAtMs = GameEngine.gameLoop.gameTime;
  }

  public get isExploding(): boolean {
    return this.explodedAtMs != null;
  }

  public get ageMs(): number {
    return GameEngine.gameLoop.gameTime - this.createdAtMs;
  }

  public markExploded(): void {
    if (this.explodedAtMs == null)
      this.explodedAtMs = GameEngine.gameLoop.gameTime;
  }

  public get explosionElapsedMs(): number {
    if (this.explodedAtMs == null) return 0;
    return GameEngine.gameLoop.gameTime - this.explodedAtMs;
  }

  public setPosition(x: number, y: number): void {
    this.position.x = x;
    this.position.y = y;
    this.mesh.position.set(x, y, 90);
  }

  public setColor(color: THREE.Color | number): void {
    const mat = this.mesh.material as THREE.MeshBasicNodeMaterial;
    if (typeof color === "number") {
      mat.color.set(color);
    } else {
      mat.color.copy(color);
    }
    mat.needsUpdate = true;
  }

  public setExplosionRing(radius: number, opacity: number): void {
    if (!this.ringMesh) {
      const geom = new THREE.RingGeometry(1.0, 1.15, 48);
      const mat = new THREE.MeshBasicNodeMaterial({
        color: new THREE.Color(0xffffff),
        transparent: true,
        opacity: 1.0,
        side: THREE.DoubleSide,
      });
      this.ringMesh = new THREE.Mesh(geom, mat);
      this.ringMesh.renderOrder = 95;
      GameEngine.renderer.registerEntity(this.ringMesh);
    }
    this.ringMesh.position.set(this.position.x, this.position.y, 95);
    const s = Math.max(0.0001, radius);
    this.ringMesh.scale.set(s, s, 1);
    const mat = this.ringMesh.material as THREE.MeshBasicNodeMaterial;
    mat.opacity = Math.max(0, Math.min(1, opacity));
    mat.needsUpdate = true;
    this.ringMesh.visible = opacity > 0.01;
  }

  public dispose(): void {
    this.drillEffect.dispose();
    disposeMesh(this.mesh);
    if (this.ringMesh) disposeMesh(this.ringMesh);
  }
}
